import React, { useState, useMemo } from 'react';
import { Table, Button, Space, Card, Tag, Typography, Tabs, Modal, Form, Input, Select, InputNumber, Radio, Divider, message, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  getModules, getCapabilities,
  addModule, updateModule, removeModule, isModuleIdentifierUnique,
  addCapability, updateCapability, removeCapability, isCapIdentifierUnique,
  TYPE_LABEL, TYPE_COLOR, DATA_TYPE_OPTIONS, PARAM_TYPE_OPTIONS, IDENT_REGEX
} from '../thingModelData';

const { Text } = Typography;

// ===== 常量 =====
const CAP_TYPE_OPTIONS = [
  { label: '属性', value: 'prop' },
  { label: '服务', value: 'svc' },
  { label: '事件', value: 'evt' },
];
const EVENT_TYPE_OPTIONS = [
  { label: '信息 (Info)', value: 'info' },
  { label: '告警 (Alarm)', value: 'alarm' },
  { label: '故障 (Fault)', value: 'fault' },
];

// ===== 工具函数 =====
function dataTypeLabel(cap) {
  if (cap.capType === 'prop') {
    const m = { enum: '枚举型', int: '数值型', boolean: '布尔型', string: '字符串' };
    return m[cap.dataDef?.dataType] || '';
  }
  if (cap.capType === 'svc') return '服务';
  if (cap.capType === 'evt') return '事件';
  return '';
}
function dataDefDetail(cap) {
  const dd = cap.dataDef;
  if (!dd) return '';
  if (cap.capType === 'prop') {
    if (dd.dataType === 'enum') return (dd.enumValues || []).map(ev => `${ev.name}(${ev.val})`).join(', ');
    if (dd.dataType === 'int') {
      let s = `${dd.min}~${dd.max}`;
      if (dd.step && dd.step !== 1) s += `, 步长${dd.step}`;
      if (dd.unit) s += ` ${dd.unit}`;
      return s;
    }
    if (dd.dataType === 'boolean') return `${dd.trueLabel || '开启'}/${dd.falseLabel || '关闭'}`;
    if (dd.dataType === 'string') return `最大${dd.limit || dd.maxLength || 64}字节`;
  }
  if (cap.capType === 'svc') return `入参${(dd.inputParams || []).length}个, 出参${(dd.outputParams || []).length}个`;
  if (cap.capType === 'evt') {
    const et = dd.eventType === 'alarm' ? '告警' : dd.eventType === 'fault' ? '故障' : '信息';
    return `${et}事件, 出参${(dd.outputParams || []).length}个`;
  }
  return '';
}

/**
 * 标准能力库页面
 * 功能：能力模块管理 + 标准能力 CRUD + 类型卡片编辑器 + 参数子编辑
 * 来源：平移 iot-platform /thing-model/capability 的 UI/UE
 */
const StandardAbility = () => {
  const [modules, setModules] = useState(() => getModules());
  const [caps, setCaps] = useState(() => getCapabilities());
  const [currentModuleId, setCurrentModuleId] = useState('all');
  const refresh = () => { setModules([...getModules()]); setCaps([...getCapabilities()]); };

  // ===== 模块弹窗 =====
  const [modModalVisible, setModModalVisible] = useState(false);
  const [editingModId, setEditingModId] = useState(null);
  const [moduleForm] = Form.useForm();

  const openAddModule = () => { setEditingModId(null); moduleForm.resetFields(); setModModalVisible(true); };
  const openEditModule = (mod) => { setEditingModId(mod.id); moduleForm.setFieldsValue(mod); setModModalVisible(true); };
  const handleModuleConfirm = () => {
    moduleForm.validateFields().then(values => {
      if (!IDENT_REGEX.test(values.identifier)) { message.warning('标识符格式不合法'); return; }
      if (!isModuleIdentifierUnique(values.identifier, editingModId || undefined)) { message.warning('模块标识已存在，请更换'); return; }
      if (editingModId) {
        updateModule(editingModId, values);
        message.success('编辑成功');
      } else {
        addModule(values);
        message.success('添加成功');
      }
      setModModalVisible(false);
      refresh();
    }).catch(() => {});
  };
  const handleDeleteModule = (mod) => {
    const capCount = caps.filter(c => c.moduleId === mod.id).length;
    Modal.confirm({
      title: '删除确认',
      content: `确定删除模块「${mod.name}」？模块下的 ${capCount} 个能力将同步删除。`,
      okText: '确定', cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => {
        removeModule(mod.id);
        if (currentModuleId === mod.id) setCurrentModuleId('all');
        refresh();
        message.success('已删除');
      },
    });
  };

  // ===== 过滤能力列表 =====
  const filteredCapabilities = useMemo(() => {
    let list = caps;
    if (currentModuleId !== 'all') list = list.filter(c => c.moduleId === currentModuleId);
    return list.map(cap => {
      const mod = modules.find(m => m.id === cap.moduleId);
      return { ...cap, _moduleName: mod ? mod.name : '—', key: cap.id };
    });
  }, [caps, modules, currentModuleId]);

  // ===== 能力弹窗 =====
  const [capModalVisible, setCapModalVisible] = useState(false);
  const [editingCapId, setEditingCapId] = useState(null);
  const [capType, setCapType] = useState('prop');
  const [dataType, setDataType] = useState('enum');
  const [accessMode, setAccessMode] = useState('rw');
  const [enumValues, setEnumValues] = useState([{ name: '', val: 0 }]);
  const [capDefaultVal, setCapDefaultVal] = useState('');
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(100);
  const [step, setStep] = useState(1);
  const [unit, setUnit] = useState('');
  const [trueLabel, setTrueLabel] = useState('开启');
  const [falseLabel, setFalseLabel] = useState('关闭');
  const [maxLength, setMaxLength] = useState(64);
  const [eventType, setEventType] = useState('alarm');
  const [inputParams, setInputParams] = useState([]);
  const [outputParams, setOutputParams] = useState([]);
  const [capName, setCapName] = useState('');
  const [capIdentifier, setCapIdentifier] = useState('');
  const [capDescr, setCapDescr] = useState('');

  const resetCapForm = () => {
    setCapType('prop'); setDataType('enum'); setAccessMode('rw');
    setEnumValues([{ name: '', val: 0 }]); setCapDefaultVal('');
    setMin(0); setMax(100); setStep(1); setUnit('');
    setTrueLabel('开启'); setFalseLabel('关闭'); setMaxLength(64);
    setEventType('alarm'); setInputParams([]); setOutputParams([]);
    setCapName(''); setCapIdentifier(''); setCapDescr('');
  };

  const handleCapTypeChange = (val) => {
    if (capName || capIdentifier) {
      Modal.confirm({
        title: '提示',
        content: '切换类型将清空当前数据定义，是否继续？',
        okText: '继续', cancelText: '取消',
        onOk: () => { setCapType(val); resetDataDef(val); },
      });
    } else {
      setCapType(val);
      resetDataDef(val);
    }
  };
  const resetDataDef = (type) => {
    if (type === 'prop') { setDataType('enum'); setEnumValues([{ name: '', val: 0 }]); setCapDefaultVal(''); setMin(0); setMax(100); setStep(1); setUnit(''); setTrueLabel('开启'); setFalseLabel('关闭'); setMaxLength(64); }
    else if (type === 'svc') { setInputParams([]); setOutputParams([]); }
    else { setEventType('alarm'); setOutputParams([]); }
  };

  const openAddCapability = () => { setEditingCapId(null); resetCapForm(); setCapModalVisible(true); };
  const openEditCapability = (cap) => {
    setEditingCapId(cap.id);
    const dd = cap.dataDef || {};
    setCapType(cap.capType);
    setCapName(cap.name); setCapIdentifier(cap.identifier); setCapDescr(cap.descr || '');
    if (cap.capType === 'prop') {
      setDataType(dd.dataType || 'enum'); setAccessMode(dd.accessMode || 'rw');
      setEnumValues(dd.enumValues ? JSON.parse(JSON.stringify(dd.enumValues)) : [{ name: '', val: 0 }]);
      setCapDefaultVal(dd.defaultVal || ''); setMin(dd.min || 0); setMax(dd.max || 100);
      setStep(dd.step || 1); setUnit(dd.unit || ''); setTrueLabel(dd.trueLabel || '开启');
      setFalseLabel(dd.falseLabel || '关闭'); setMaxLength(dd.limit || dd.maxLength || 64);
    } else if (cap.capType === 'svc') {
      setInputParams(JSON.parse(JSON.stringify(dd.inputParams || [])));
      setOutputParams(JSON.parse(JSON.stringify(dd.outputParams || [])));
    } else {
      setEventType(dd.eventType || 'alarm');
      setOutputParams(JSON.parse(JSON.stringify(dd.outputParams || [])));
    }
    setCapModalVisible(true);
  };

  const buildDataDef = () => {
    if (capType === 'prop') {
      const dd = { dataType, accessMode, defaultVal: capDefaultVal };
      if (dataType === 'enum') dd.enumValues = enumValues.filter(ev => ev.name.trim());
      else if (dataType === 'int') { dd.min = min; dd.max = max; dd.step = step; dd.unit = unit; }
      else if (dataType === 'boolean') { dd.trueLabel = trueLabel; dd.falseLabel = falseLabel; }
      else if (dataType === 'string') dd.limit = maxLength;
      return dd;
    }
    if (capType === 'svc') return { dataType: 'service', inputParams, outputParams };
    return { dataType: 'event', eventType, outputParams };
  };

  const getCapModuleId = () => {
    if (editingCapId) {
      return caps.find(c => c.id === editingCapId)?.moduleId;
    }
    if (currentModuleId !== 'all') return currentModuleId;
    return modules[0]?.id;
  };

  const handleCapabilityConfirm = () => {
    if (!capName.trim()) { message.warning('请输入能力名称'); return; }
    if (!capIdentifier.trim()) { message.warning('请输入标识符'); return; }
    if (!IDENT_REGEX.test(capIdentifier)) { message.warning('标识符格式不合法'); return; }
    if (!isCapIdentifierUnique(capIdentifier, editingCapId || undefined)) { message.warning('能力标识符已存在，请更换'); return; }
    if (capType === 'prop') {
      if (dataType === 'enum' && enumValues.filter(ev => ev.name.trim()).length < 1) { message.warning('枚举值至少需要一个有效条目'); return; }
      if (dataType === 'int' && max <= min) { message.warning('最大值必须大于最小值'); return; }
    }
    if (capType === 'evt' && !eventType) { message.warning('请选择事件类型'); return; }

    const item = { moduleId: getCapModuleId(), capType, name: capName.trim(), identifier: capIdentifier.trim(), descr: capDescr.trim(), dataDef: buildDataDef() };
    if (editingCapId) { updateCapability(editingCapId, item); message.success('编辑成功'); }
    else { addCapability(item); message.success('添加成功'); }
    setCapModalVisible(false);
    refresh();
  };

  const handleDeleteCapability = (cap) => {
    Modal.confirm({
      title: '删除确认',
      content: `确定删除能力「${cap.name}」？`,
      okText: '确定', cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => { removeCapability(cap.id); refresh(); message.success('已删除'); },
    });
  };

  // ===== 参数子弹窗 =====
  const [paramVisible, setParamVisible] = useState(false);
  const [paramTarget, setParamTarget] = useState('input');
  const [editingParamIdx, setEditingParamIdx] = useState(-1);
  const [pName, setPName] = useState('');
  const [pIdentifier, setPIdentifier] = useState('');
  const [pDataType, setPDataType] = useState('boolean');
  const [pDefaultVal, setPDefaultVal] = useState('');
  const [pMin, setPMin] = useState(0);
  const [pMax, setPMax] = useState(100);
  const [pStep, setPStep] = useState(1);
  const [pUnit, setPUnit] = useState('');
  const [pMaxLen, setPMaxLen] = useState(64);
  const [pTrueLabel, setPTrueLabel] = useState('是');
  const [pFalseLabel, setPFalseLabel] = useState('否');
  const [pEnumValues, setPEnumValues] = useState([{ name: '', val: 0 }]);

  const resetParamForm = () => {
    setPName(''); setPIdentifier(''); setPDataType('boolean'); setPDefaultVal('');
    setPMin(0); setPMax(100); setPStep(1); setPUnit('');
    setPMaxLen(64); setPTrueLabel('是'); setPFalseLabel('否');
    setPEnumValues([{ name: '', val: 0 }]);
  };

  const openAddParam = (target) => { setParamTarget(target); setEditingParamIdx(-1); resetParamForm(); setParamVisible(true); };
  const openEditParam = (target, idx) => {
    setParamTarget(target); setEditingParamIdx(idx);
    const ps = target === 'input' ? inputParams : outputParams;
    const p = ps[idx] || {};
    setPName(p.name || ''); setPIdentifier(p.identifier || ''); setPDataType(p.dataType || 'boolean');
    setPDefaultVal(p.defaultVal || ''); setPMin(p.min || 0); setPMax(p.max || 100);
    setPStep(p.step || 1); setPUnit(p.unit || ''); setPMaxLen(p.maxLength || 64);
    setPTrueLabel(p.trueLabel || '是'); setPFalseLabel(p.falseLabel || '否');
    setPEnumValues(p.enumValues ? JSON.parse(JSON.stringify(p.enumValues)) : [{ name: '', val: 0 }]);
    setParamVisible(true);
  };

  const buildParamObj = () => {
    const p = { name: pName.trim(), identifier: pIdentifier.trim(), dataType: pDataType, defaultVal: pDefaultVal };
    if (pDataType === 'int') { p.min = pMin; p.max = pMax; p.step = pStep; p.unit = pUnit; }
    else if (pDataType === 'string') { p.maxLength = pMaxLen; }
    else if (pDataType === 'boolean') { p.trueLabel = pTrueLabel; p.falseLabel = pFalseLabel; }
    else if (pDataType === 'enum') { p.enumValues = pEnumValues.filter(ev => ev.name.trim()); }
    return p;
  };

  const handleParamConfirm = () => {
    if (!pName.trim() || !pIdentifier.trim()) { message.warning('请填写完整参数信息'); return; }
    const param = buildParamObj();
    if (paramTarget === 'input') {
      if (editingParamIdx >= 0) { const arr = [...inputParams]; arr[editingParamIdx] = param; setInputParams(arr); }
      else setInputParams([...inputParams, param]);
    } else {
      if (editingParamIdx >= 0) { const arr = [...outputParams]; arr[editingParamIdx] = param; setOutputParams(arr); }
      else setOutputParams([...outputParams, param]);
    }
    setParamVisible(false);
  };

  const typeTag = (t) => {
    const clr = TYPE_COLOR[t] || { bg: '#f0f0f0', color: '#999' };
    return <Tag style={{ background: clr.bg, color: clr.color, border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 500, margin: 0 }}>{TYPE_LABEL[t] || t}</Tag>;
  };

  const paramTypeLabel = (t) => ({ int: 'Int', string: 'String', boolean: 'Boolean', enum: 'Enum' }[t] || t);

  // ===== 表格列 =====
  const columns = [
    { title: '类型', dataIndex: 'capType', key: 'capType', width: 80, align: 'center', render: (t) => typeTag(t) },
    { title: '能力名称', dataIndex: 'name', key: 'name', minWidth: 120, render: (t) => <Text strong>{t}</Text> },
    { title: '能力标识', dataIndex: 'identifier', key: 'identifier', width: 170, render: (t) => <Text style={{ fontFamily: 'monospace', fontSize: 13, color: '#666' }}>{t}</Text> },
    { title: '数据类型', key: 'dataType', width: 100, align: 'center', render: (_, r) => <Text style={{ fontSize: 13, color: '#555' }}>{dataTypeLabel(r)}</Text> },
    { title: '数据定义', key: 'dataDef', minWidth: 200, ellipsis: true, render: (_, r) => <Text style={{ fontSize: 12, color: '#888' }}>{dataDefDetail(r)}</Text> },
    { title: '描述', dataIndex: 'descr', key: 'descr', minWidth: 140, ellipsis: true, render: (t) => t ? <Text style={{ fontSize: 13, color: '#666' }}>{t}</Text> : <Text type="secondary">—</Text> },
    { title: '所属模块', key: '_moduleName', width: 130, align: 'center', render: (_, r) => <Text style={{ fontSize: 13 }}>{r._moduleName}</Text> },
    {
      title: '操作', key: 'action', width: 150, fixed: 'right',
      render: (_, record) => (
        <Space size={0} split={<span style={{ color: '#d9d9d9', margin: '0 6px' }}>|</span>}>
          <Button type="link" size="small" onClick={() => openEditCapability(record)}>编辑</Button>
          <Button type="link" size="small" danger onClick={() => handleDeleteCapability(record)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px - 48px - 24px)', overflow: 'hidden' }}>
      <Tabs
        activeKey="capability"
        items={[{ key: 'capability', label: '标准能力' }]}
        style={{ marginBottom: 0 }}
        tabBarStyle={{ padding: '0 24px', background: '#fff', borderBottom: '1px solid #f0f0f0', marginBottom: 0 }}
      />

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* 左侧模块栏 */}
        <div style={{ width: 260, flexShrink: 0, background: '#fff', borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
            <Text strong style={{ fontSize: 14 }}>能力模块</Text>
            <Button type="link" size="small" onClick={openAddModule}>+ 添加</Button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
            {/* 全部 */}
            <div
              onClick={() => setCurrentModuleId('all')}
              style={{
                padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                fontSize: 13, fontWeight: 500,
                background: currentModuleId === 'all' ? '#e6f4ff' : 'transparent',
                color: currentModuleId === 'all' ? '#1677ff' : '#333',
              }}
            >全部</div>
            {/* 模块项 */}
            {modules.map(mod => (
              <div
                key={mod.id}
                onClick={() => setCurrentModuleId(mod.id)}
                onMouseEnter={e => {
                  const actions = e.currentTarget.querySelector('.mod-actions');
                  if (actions) actions.style.display = 'flex';
                }}
                onMouseLeave={e => {
                  const actions = e.currentTarget.querySelector('.mod-actions');
                  if (actions) actions.style.display = 'none';
                }}
                style={{
                  display: 'flex', alignItems: 'center', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  marginBottom: 2, transition: 'all .15s',
                  background: currentModuleId === mod.id ? '#e6f4ff' : 'transparent',
                  position: 'relative',
                }}
              >
                <span style={{
                  flex: 1, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  color: currentModuleId === mod.id ? '#1677ff' : '#333',
                  fontWeight: currentModuleId === mod.id ? 500 : 400,
                }}>{mod.name}</span>
                <span style={{ fontSize: 11, color: '#bfbfbf', marginLeft: 8, flexShrink: 0 }}>{mod.identifier}</span>
                <span
                  className="mod-actions"
                  style={{ display: 'none', gap: 2, marginLeft: 4 }}
                  onClick={e => e.stopPropagation()}
                >
                  <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditModule(mod)} />
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteModule(mod)} />
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 右侧表格 */}
        <div style={{ flex: 1, padding: '16px 24px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ marginBottom: 16 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAddCapability}>添加标准能力</Button>
          </div>
          <Card bordered={false} style={{ flex: 1, overflow: 'hidden' }}>
            <Table
              columns={columns}
              dataSource={filteredCapabilities}
              rowKey="id"
              scroll={{ x: 1000 }}
              locale={{ emptyText: <Empty description="暂无标准能力" /> }}
              pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
            />
          </Card>
        </div>
      </div>

      {/* 模块弹窗 */}
      <Modal
        title={editingModId ? '编辑能力模块' : '添加能力模块'}
        open={modModalVisible}
        onCancel={() => setModModalVisible(false)}
        onOk={handleModuleConfirm}
        okText="确定" cancelText="取消"
        destroyOnClose
        width={480}
      >
        <Form form={moduleForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="模块名称" rules={[{ required: true, message: '请输入模块名称' }]}>
            <Input placeholder="请输入模块名称" />
          </Form.Item>
          <Form.Item
            name="identifier" label="模块标识"
            rules={[
              { required: true, message: '请输入模块标识' },
              { pattern: IDENT_REGEX, message: '字母开头，仅允许字母、数字、下划线' },
            ]}
            extra={<span style={{ fontSize: 12, color: '#999' }}>字母开头，仅允许字母、数字、下划线，全局唯一</span>}
          >
            <Input placeholder="例如 WorkModeModule" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 能力弹窗 */}
      <Modal
        title={editingCapId ? '编辑标准能力' : '添加标准能力'}
        open={capModalVisible}
        onCancel={() => setCapModalVisible(false)}
        width={860}
        style={{ top: 24 }}
        footer={null}
        destroyOnClose
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 4 }}>
          {/* 能力类型 */}
          <div style={{ marginBottom: 18 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>能力类型 <span style={{ color: '#ff4d4f' }}>*</span></Text>
            <Space>
              {CAP_TYPE_OPTIONS.map(t => (
                <div
                  key={t.value}
                  onClick={() => handleCapTypeChange(t.value)}
                  style={{
                    padding: '0 16px', lineHeight: '30px', border: `1px solid ${capType === t.value ? '#1677ff' : '#d9d9d9'}`,
                    borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500,
                    color: capType === t.value ? '#1677ff' : '#666',
                    background: capType === t.value ? '#e6f4ff' : '#fafafa',
                    transition: 'all .15s',
                  }}
                >{t.label}</div>
              ))}
            </Space>
          </div>

          {/* 基础字段 */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>能力名称 <span style={{ color: '#ff4d4f' }}>*</span></Text>
              <Input value={capName} onChange={e => setCapName(e.target.value)} placeholder="请输入能力名称（2-50字符）" maxLength={50} />
            </div>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>标识符 <span style={{ color: '#ff4d4f' }}>*</span></Text>
              <Input value={capIdentifier} onChange={e => setCapIdentifier(e.target.value)} placeholder="例如 WorkMode" maxLength={40} />
              <Text type="secondary" style={{ fontSize: 12 }}>字母开头，仅允许字母、数字、下划线，全局唯一</Text>
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>描述</Text>
            <Input.TextArea value={capDescr} onChange={e => setCapDescr(e.target.value)} placeholder="请输入能力描述" rows={2} />
          </div>

          {/* 属性专属 */}
          {capType === 'prop' && (
            <>
              <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>数据类型 <span style={{ color: '#ff4d4f' }}>*</span></Text>
                  <Select
                    style={{ width: '100%' }}
                    value={dataType}
                    onChange={(val) => { setDataType(val); setCapDefaultVal(''); }}
                    options={DATA_TYPE_OPTIONS}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>读写模式</Text>
                  <Select
                    style={{ width: '100%' }}
                    value={accessMode}
                    onChange={setAccessMode}
                    options={[
                      { value: 'rw', label: '读写 (Read/Write)' },
                      { value: 'ro', label: '只读 (Read Only)' },
                    ]}
                  />
                </div>
              </div>

              <Divider>数据定义</Divider>

              {/* 枚举型 */}
              {dataType === 'enum' && (
                <>
                  <div style={{ marginBottom: 18 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>枚举值定义 <span style={{ color: '#ff4d4f' }}>*</span></Text>
                    {enumValues.map((ev, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <Input value={ev.name} onChange={e => { const arr = [...enumValues]; arr[i].name = e.target.value; setEnumValues(arr); }} placeholder="名称（如：低功耗模式）" style={{ flex: 1 }} />
                        <InputNumber value={ev.val} onChange={v => { const arr = [...enumValues]; arr[i].val = v; setEnumValues(arr); }} min={0} style={{ width: 100 }} />
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => setEnumValues(enumValues.filter((_, idx) => idx !== i))} />
                      </div>
                    ))}
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setEnumValues([...enumValues, { name: '', val: enumValues.length }])}>+ 添加枚举值</Button>
                  </div>
                  <div style={{ marginBottom: 18 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>默认值</Text>
                    <Input value={capDefaultVal} onChange={e => setCapDefaultVal(e.target.value)} placeholder="请输入默认枚举值的 Value" />
                  </div>
                </>
              )}

              {/* 数值型 */}
              {dataType === 'int' && (
                <>
                  <div style={{ marginBottom: 18 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>取值范围 <span style={{ color: '#ff4d4f' }}>*</span></Text>
                    <Space>
                      <InputNumber value={min} onChange={v => setMin(v)} max={max - 1} placeholder="最小值" style={{ flex: 1, width: 160 }} />
                      <span style={{ color: '#bfbfbf' }}>~</span>
                      <InputNumber value={max} onChange={v => setMax(v)} min={min + 1} placeholder="最大值" style={{ flex: 1, width: 160 }} />
                    </Space>
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>步长</Text>
                      <InputNumber value={step} onChange={setStep} min={1} style={{ width: 150 }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>单位</Text>
                      <Input value={unit} onChange={e => setUnit(e.target.value)} placeholder="例如：%、°C、ms" style={{ width: 150 }} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 18 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>默认值</Text>
                    <InputNumber value={capDefaultVal} onChange={setCapDefaultVal} min={min} max={max} style={{ width: 200 }} />
                  </div>
                </>
              )}

              {/* 布尔型 */}
              {dataType === 'boolean' && (
                <>
                  <div style={{ marginBottom: 18 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>布尔值标签</Text>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>true 标签</Text>
                        <Input value={trueLabel} onChange={e => setTrueLabel(e.target.value)} placeholder="开启" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>false 标签</Text>
                        <Input value={falseLabel} onChange={e => setFalseLabel(e.target.value)} placeholder="关闭" />
                      </div>
                    </div>
                  </div>
                  <div style={{ marginBottom: 18 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>默认值</Text>
                    <Radio.Group value={capDefaultVal} onChange={e => setCapDefaultVal(e.target.value)}>
                      <Radio value="1">{trueLabel || '开启'} (true)</Radio>
                      <Radio value="0">{falseLabel || '关闭'} (false)</Radio>
                    </Radio.Group>
                  </div>
                </>
              )}

              {/* 字符串型 */}
              {dataType === 'string' && (
                <>
                  <div style={{ marginBottom: 18 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>最大长度（字节） <span style={{ color: '#ff4d4f' }}>*</span></Text>
                    <InputNumber value={maxLength} onChange={setMaxLength} min={1} max={65535} style={{ width: 150 }} />
                  </div>
                  <div style={{ marginBottom: 18 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>默认值</Text>
                    <Input value={capDefaultVal} onChange={e => setCapDefaultVal(e.target.value)} placeholder="请输入默认值" />
                  </div>
                </>
              )}
            </>
          )}

          {/* 服务专属 */}
          {capType === 'svc' && (
            <>
              <Divider>数据定义</Divider>
              <div style={{ marginBottom: 18 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>输入参数</Text>
                {inputParams.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 6, marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <Text style={{ fontSize: 13 }}>{p.name || '未命名'}</Text>
                      <Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>{p.identifier} · {paramTypeLabel(p.dataType)}</Text>
                    </div>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditParam('input', i)} />
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => setInputParams(inputParams.filter((_, idx) => idx !== i))} />
                  </div>
                ))}
                {inputParams.length === 0 && <Text type="secondary" style={{ fontSize: 12, display: 'block', padding: '8px 0' }}>暂无输入参数</Text>}
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openAddParam('input')}>+ 添加参数</Button>
              </div>
              <div style={{ marginBottom: 18 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>输出参数</Text>
                {outputParams.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 6, marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <Text style={{ fontSize: 13 }}>{p.name || '未命名'}</Text>
                      <Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>{p.identifier} · {paramTypeLabel(p.dataType)}</Text>
                    </div>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditParam('output', i)} />
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => setOutputParams(outputParams.filter((_, idx) => idx !== i))} />
                  </div>
                ))}
                {outputParams.length === 0 && <Text type="secondary" style={{ fontSize: 12, display: 'block', padding: '8px 0' }}>暂无输出参数</Text>}
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openAddParam('output')}>+ 添加参数</Button>
              </div>
            </>
          )}

          {/* 事件专属 */}
          {capType === 'evt' && (
            <>
              <Divider>数据定义</Divider>
              <div style={{ marginBottom: 18 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>事件类型 <span style={{ color: '#ff4d4f' }}>*</span></Text>
                <Space>
                  {EVENT_TYPE_OPTIONS.map(et => (
                    <div
                      key={et.value}
                      onClick={() => setEventType(et.value)}
                      style={{
                        padding: '0 16px', lineHeight: '30px', border: `1px solid ${eventType === et.value ? '#1677ff' : '#d9d9d9'}`,
                        borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500,
                        color: eventType === et.value ? '#1677ff' : '#666',
                        background: eventType === et.value ? '#e6f4ff' : '#fafafa',
                      }}
                    >{et.label}</div>
                  ))}
                </Space>
              </div>
              <div style={{ marginBottom: 18 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>输出参数</Text>
                {outputParams.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 6, marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <Text style={{ fontSize: 13 }}>{p.name || '未命名'}</Text>
                      <Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>{p.identifier} · {paramTypeLabel(p.dataType)}</Text>
                    </div>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditParam('output', i)} />
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => setOutputParams(outputParams.filter((_, idx) => idx !== i))} />
                  </div>
                ))}
                {outputParams.length === 0 && <Text type="secondary" style={{ fontSize: 12, display: 'block', padding: '8px 0' }}>暂无输出参数</Text>}
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openAddParam('output')}>+ 添加参数</Button>
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setCapModalVisible(false)}>取消</Button>
          <Button type="primary" onClick={handleCapabilityConfirm}>提交</Button>
        </div>
      </Modal>

      {/* 参数子弹窗 */}
      <Modal
        title={`${paramTarget === 'input' ? '输入' : '输出'}参数`}
        open={paramVisible}
        onCancel={() => setParamVisible(false)}
        width={600}
        footer={null}
        destroyOnClose
        zIndex={1050}
      >
        <div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>参数名称 <span style={{ color: '#ff4d4f' }}>*</span></Text>
              <Input value={pName} onChange={e => setPName(e.target.value)} placeholder="请输入参数名称" />
            </div>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>参数标识 <span style={{ color: '#ff4d4f' }}>*</span></Text>
              <Input value={pIdentifier} onChange={e => setPIdentifier(e.target.value)} placeholder="例如 result" />
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>数据类型 <span style={{ color: '#ff4d4f' }}>*</span></Text>
            <Select style={{ width: '100%' }} value={pDataType} onChange={(v) => { setPDataType(v); setPDefaultVal(''); }} options={PARAM_TYPE_OPTIONS} />
          </div>

          <Divider>类型定义</Divider>

          {/* Int */}
          {pDataType === 'int' && (
            <>
              <div style={{ marginBottom: 18 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>取值范围</Text>
                <Space>
                  <InputNumber value={pMin} onChange={setPMin} max={pMax - 1} style={{ flex: 1, width: 160 }} />
                  <span style={{ color: '#bfbfbf' }}>~</span>
                  <InputNumber value={pMax} onChange={setPMax} min={pMin + 1} style={{ flex: 1, width: 160 }} />
                </Space>
              </div>
              <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>步长</Text>
                  <InputNumber value={pStep} onChange={setPStep} min={1} style={{ width: 150 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>单位</Text>
                  <Input value={pUnit} onChange={e => setPUnit(e.target.value)} placeholder="例如：%、°C、ms" style={{ width: 150 }} />
                </div>
              </div>
              <div style={{ marginBottom: 18 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>默认值</Text>
                <InputNumber value={pDefaultVal} onChange={setPDefaultVal} min={pMin} max={pMax} style={{ width: 200 }} />
              </div>
            </>
          )}

          {/* String */}
          {pDataType === 'string' && (
            <>
              <div style={{ marginBottom: 18 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>最大长度（字节）</Text>
                <InputNumber value={pMaxLen} onChange={setPMaxLen} min={1} max={65535} style={{ width: 150 }} />
              </div>
              <div style={{ marginBottom: 18 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>默认值</Text>
                <Input value={pDefaultVal} onChange={e => setPDefaultVal(e.target.value)} placeholder="请输入默认值" />
              </div>
            </>
          )}

          {/* Boolean */}
          {pDataType === 'boolean' && (
            <>
              <div style={{ marginBottom: 18 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>布尔值标签</Text>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>true 标签</Text>
                    <Input value={pTrueLabel} onChange={e => setPTrueLabel(e.target.value)} placeholder="是" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>false 标签</Text>
                    <Input value={pFalseLabel} onChange={e => setPFalseLabel(e.target.value)} placeholder="否" />
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: 18 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>默认值</Text>
                <Radio.Group value={pDefaultVal} onChange={e => setPDefaultVal(e.target.value)}>
                  <Radio value="1">{pTrueLabel || '是'} (true)</Radio>
                  <Radio value="0">{pFalseLabel || '否'} (false)</Radio>
                </Radio.Group>
              </div>
            </>
          )}

          {/* Enum */}
          {pDataType === 'enum' && (
            <>
              <div style={{ marginBottom: 18 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>枚举值定义</Text>
                {pEnumValues.map((ev, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <Input value={ev.name} onChange={e => { const arr = [...pEnumValues]; arr[i].name = e.target.value; setPEnumValues(arr); }} placeholder="名称" style={{ flex: 1 }} />
                    <InputNumber value={ev.val} onChange={v => { const arr = [...pEnumValues]; arr[i].val = v; setPEnumValues(arr); }} min={0} style={{ width: 100 }} />
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => setPEnumValues(pEnumValues.filter((_, idx) => idx !== i))} />
                  </div>
                ))}
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setPEnumValues([...pEnumValues, { name: '', val: pEnumValues.length }])}>+ 添加枚举值</Button>
              </div>
              <div style={{ marginBottom: 18 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>默认值</Text>
                <Input value={pDefaultVal} onChange={e => setPDefaultVal(e.target.value)} placeholder="请输入默认枚举值的 Value" />
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setParamVisible(false)}>取消</Button>
          <Button type="primary" onClick={handleParamConfirm}>确定</Button>
        </div>
      </Modal>
    </div>
  );
};

export default StandardAbility;
